/**
 * IaVideo Component - Simple Two-State Architecture
 * 
 * States:
 * - PREVIEW: Muted mode, button visible (muted autoplay, hover preview)
 * - PLAYING_WITH_SOUND: Sound enabled, button hidden
 * 
 * Modes determine preview behavior:
 * - background: Auto-plays muted, no interaction
 * - hover: Plays muted on hover
 * - autoplay: Auto-plays muted (default)
 * - sound: Starts with sound enabled
 */

// ============================================================================
// VIDEO CONTROLLER
// ============================================================================

class VideoController {
  constructor(desktopVideo, mobileVideo, showHtmlControls = false) {
    this.desktopVideo = desktopVideo;
    this.mobileVideo = mobileVideo;
    this.showHtmlControls = showHtmlControls;
    this.activeVideo = null;
    this.updateActiveVideo();
  }
  
  updateActiveVideo() {
    const isDesktop = window.matchMedia("(min-width: 750px)").matches;
    const previousVideo = this.activeVideo;
    
    if (isDesktop && this.desktopVideo) {
      this.activeVideo = this.desktopVideo;
      this.mobileVideo?.pause();
    } else if (!isDesktop && this.mobileVideo) {
      this.activeVideo = this.mobileVideo;
      this.desktopVideo?.pause();
    } else {
      this.activeVideo = this.desktopVideo;
      this.mobileVideo?.pause();
    }
    
    return previousVideo !== this.activeVideo;
  }
  
  play(withSound = false) {
    if (!this.activeVideo) return Promise.resolve();
    
    // Show HTML controls only when playing with sound
    if (this.showHtmlControls && withSound) {
      this.activeVideo.setAttribute("controls", "true");
    } else if (this.showHtmlControls) {
      this.activeVideo.removeAttribute("controls");
    }
    
    // Play the video - ensure it actually starts playing
    const playPromise = this.activeVideo.play();
    
    if (playPromise !== undefined) {
      return playPromise
        .then(() => {
          // Ensure video is actually playing (some browsers might pause after setting controls)
          if (this.activeVideo.paused) {
            return this.activeVideo.play().catch(() => {});
          }
        })
        .catch(() => {
          // If play fails, try once more
          return this.activeVideo.play().catch(() => {});
        });
    }
    
    return Promise.resolve();
  }
  
  pause() {
    if (this.activeVideo && !this.activeVideo.paused) {
      this.activeVideo.pause();
    }
  }
  
  reset() {
    if (this.activeVideo) {
      this.activeVideo.currentTime = 0;
    }
  }
  
  mute() {
    if (this.activeVideo) this.activeVideo.muted = true;
  }
  
  unmute() {
    if (this.activeVideo) this.activeVideo.muted = false;
  }
  
  get isPaused() {
    return this.activeVideo?.paused ?? true;
  }
  
  cleanup() {
    // Remove controls attribute on cleanup
    this.desktopVideo?.removeAttribute("controls");
    this.mobileVideo?.removeAttribute("controls");
  }
}

// ============================================================================
// PLAYBACK STRATEGIES
// ============================================================================

class PlaybackStrategy {
  constructor(controller, ui) {
    this.controller = controller;
    this.ui = ui;
    this.isInViewport = false;
    this.state = 'preview'; // preview | playing_with_sound
  }
  
  enterPreviewMode() {
    this.state = 'preview';
    this.controller.mute();
  }
  
  enterSoundMode() {
    this.state = 'playing_with_sound';
    this.controller.unmute();
  }
  
  isPreview() {
    return this.state === 'preview';
  }
  
  onEnterViewport() {
    this.isInViewport = true;
  }
  
  onExitViewport() {
    this.isInViewport = false;
  }
  
  onClick() {}
  onHover() {}
  onHoverEnd() {}
  onVideoPause() {}
  
  shouldShowButton() {
    // Hide button if HTML controls are enabled and we're in sound mode
    if (this.controller.showHtmlControls && !this.isPreview()) {
      return false;
    }
    return this.isPreview();
  }
  
  shouldShowAsPlaying() {
    return !this.isPreview() && !this.controller.isPaused;
  }
}

// Background: Always muted, no interaction
class BackgroundStrategy extends PlaybackStrategy {
  onEnterViewport() {
    super.onEnterViewport();
    this.enterPreviewMode();
    this.controller.reset();
    this.controller.play(false);
  }
  
  onExitViewport() {
    super.onExitViewport();
    this.controller.pause();
  }
  
  onVideoPause() {
    if (this.isInViewport) {
      this.controller.reset();
      this.controller.play(false);
    }
  }
}

// Hover: Preview on hover
class HoverStrategy extends PlaybackStrategy {
  constructor(controller, ui) {
    super(controller, ui);
    this.wasManuallyPaused = false; // Track if video was manually paused by user
    this.isAutoPausing = false; // Track if we're doing an automatic pause
  }
  
  onEnterViewport() {
    super.onEnterViewport();
    this.enterPreviewMode();
    this.wasManuallyPaused = false; // Reset flag on viewport enter
    if (!this.controller.showHtmlControls) {
      this.controller.reset();
    }
  }
  
  onExitViewport() {
    super.onExitViewport();
    this.isAutoPausing = true;
    this.controller.pause();
    this.isAutoPausing = false;
    this.enterPreviewMode(); // Reset to preview
    this.wasManuallyPaused = false; // Reset flag on viewport exit
  }
  
  onVideoPause() {
    // If pause happened and it wasn't from our automatic pause, it was manual
    if (this.isInViewport && this.isPreview() && !this.isAutoPausing) {
      this.wasManuallyPaused = true;
    }
    
    // Only auto-reset if not manually paused and HTML controls disabled
    if (this.isInViewport && this.isPreview() && !this.controller.showHtmlControls && !this.wasManuallyPaused) {
      this.controller.reset();
    }
  }
  
  onHover() {
    // Don't handle hover if HTML controls are enabled and we're in sound mode
    if (this.controller.showHtmlControls && !this.isPreview()) {
      return;
    }
    
    if (this.isInViewport) {
      this.enterPreviewMode();
      
      // Always reset to 0 when hover starts playback, unless it was manually paused
      if (!this.wasManuallyPaused) {
        this.controller.reset();
      }
      
      this.wasManuallyPaused = false; // Reset flag when starting hover playback
      this.controller.play(false);
    }
  }
  
  onHoverEnd() {
    // Don't handle hover end if HTML controls are enabled and we're in sound mode
    if (this.controller.showHtmlControls && !this.isPreview()) {
      return;
    }
    
    if (this.isPreview()) {
      // This is an automatic pause from hover end - always reset to 0
      this.isAutoPausing = true;
      this.controller.pause();
      this.isAutoPausing = false;
      this.controller.reset(); // Always reset to 0 when leaving hover
      // Don't set wasManuallyPaused - this was automatic
    }
  }
  
  onClick() {
    if (this.isPreview()) {
      // Switch to sound mode
      this.enterSoundMode();
      
      // Reset to 0 when starting play with sound, but only if it wasn't manually paused
      if (!this.wasManuallyPaused) {
        this.controller.reset();
      }
      
      this.wasManuallyPaused = false; // Reset flag when switching to sound
      
      // Ensure video plays - wait for play promise to resolve
      this.controller.play(true).then(() => {
        // Double-check video is actually playing after controls are added
        if (this.controller.activeVideo && this.controller.activeVideo.paused) {
          this.controller.activeVideo.play().catch(() => {});
        }
      });
      
      this.ui.pauseOtherVideos();
    } else {
      // If HTML controls enabled, don't handle click (let native controls handle it)
      if (this.controller.showHtmlControls) {
        return;
      }
      // Return to preview mode
      this.enterPreviewMode();
      this.controller.pause();
      this.wasManuallyPaused = true; // User manually paused
      this.controller.reset();
    }
  }
}

// Autoplay: Muted autoplay
class AutoplayStrategy extends PlaybackStrategy {
  constructor(controller, ui, autoplay = true) {
    super(controller, ui);
    this.autoplay = autoplay;
  }
  
  onEnterViewport() {
    super.onEnterViewport();
    // Only auto-play if autoplay is enabled
    if (!this.autoplay) {
      return;
    }
    this.enterPreviewMode();
    if (!this.controller.showHtmlControls) {
      this.controller.reset();
    }
    this.controller.play(false);
  }
  
  onExitViewport() {
    super.onExitViewport();
    this.controller.pause();
    this.enterPreviewMode(); // Reset to preview
  }
  
  onVideoPause() {
    // Only auto-resume if autoplay is enabled
    if (!this.autoplay) {
      return;
    }
    if (this.isInViewport && this.isPreview() && !this.controller.showHtmlControls) {
      this.controller.reset();
      this.controller.play(false);
    }
  }
  
  onClick() {
    if (this.isPreview()) {
      // Switch to sound mode
      const wasPlaying = !this.controller.isPaused;
      this.enterSoundMode();
      
      // Only reset if not already playing or if HTML controls disabled
      if (!wasPlaying || !this.controller.showHtmlControls) {
        this.controller.reset();
      }
      
      this.controller.play(true);
      this.ui.pauseOtherVideos();
    } else {
      // Toggle play/pause or return to preview
      if (this.controller.isPaused) {
        if (!this.controller.showHtmlControls) {
          this.controller.reset();
        }
        this.controller.play(true);
      } else {
        this.enterPreviewMode();
        this.controller.pause();
        if (!this.controller.showHtmlControls) {
          this.controller.reset();
        }
      }
    }
  }
}

// Sound: Starts with sound
class SoundStrategy extends PlaybackStrategy {
  constructor(controller, ui) {
    super(controller, ui);
    this.state = 'playing_with_sound'; // Start in sound mode
  }
  
  onEnterViewport() {
    super.onEnterViewport();
    this.controller.unmute();
    if (!this.controller.showHtmlControls) {
      this.controller.reset();
    }
    this.controller.play(true);
  }
  
  onExitViewport() {
    super.onExitViewport();
    this.controller.pause();
  }
  
  onClick() {
    if (this.controller.isPaused) {
      if (!this.controller.showHtmlControls) {
        this.controller.reset();
      }
      this.controller.play(true);
      this.ui.pauseOtherVideos();
    } else {
      this.controller.pause();
    }
  }
  
  shouldShowButton() {
    return this.controller.isPaused;
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

class IaVideo extends HTMLElement {
  connectedCallback() {
    this.init();
    this.setupListeners();
    // Only set up intersection observer if observable is enabled
    if (this.dataset.observable !== "false") {
      this.setupIntersectionObserver();
    }
    window.addEventListener("resize", this.onResize);
  }
  
  disconnectedCallback() {
    this.cleanup();
  }
  
  cleanup() {
    // Disconnect observer
    this.observer?.disconnect();
    
    // Remove window event listeners
    window.removeEventListener("resize", this.onResize);
    
    // Remove component event listeners
    this.removeEventListener("click", this.onClick);
    this.removeEventListener("mouseenter", this.onHover);
    this.removeEventListener("mouseleave", this.onHoverEnd);
    
    // Remove video wrap listeners
    if (this.videoWrapHandlers) {
      const videoWrap = this.closest("[data-video-wrap]");
      if (videoWrap) {
        videoWrap.removeEventListener("mouseenter", this.videoWrapHandlers.handleMouseEnter);
        videoWrap.removeEventListener("mouseleave", this.videoWrapHandlers.handleMouseLeave);
      }
    }
    
    // Remove video event listeners
    [this.desktopVideo, this.mobileVideo].filter(Boolean).forEach(video => {
      video.removeEventListener("pause", this.onVideoPause);
      video.removeEventListener("play", this.onVideoPlay);
      video.removeEventListener("ended", this.onVideoEnd);
    });
    
    // Cleanup controller
    this.controller?.cleanup();
  }
  
  init() {
    this.playBtn = this.querySelector("[data-play-btn]");
    this.desktopVideo = this.querySelector(".js-desktop-video");
    this.mobileVideo = this.querySelector(".js-mobile-video");
    this.showHtmlControls = this.dataset.showHtmlControls === "true";
    this.controller = new VideoController(this.desktopVideo, this.mobileVideo, this.showHtmlControls);
    this.strategy = this.createStrategy();
    
    this.render();
  }
  
  createStrategy() {
    const ui = {
      pauseOtherVideos: () => this.pauseOtherVideos(),
      updateUI: () => this.render()
    };
    
    const autoplay = this.dataset.autoplay !== "false";
    
    if (this.dataset.backgroundMode === "true") {
      return new BackgroundStrategy(this.controller, ui);
    } else if (this.dataset.hoverPreview === "true") {
      return new HoverStrategy(this.controller, ui);
    } else if (this.dataset.sound === "true") {
      return new SoundStrategy(this.controller, ui);
    } else {
      return new AutoplayStrategy(this.controller, ui, autoplay);
    }
  }
  
  onClick = (event) => {
    // The explicit play button always works; only "click the video body to
    // toggle sound" is gated by interactable. Programmatic calls (no event,
    // e.g. pauseOtherVideos) are never blocked.
    if (event) {
      const fromPlayBtn = this.playBtn && event.target.closest("[data-play-btn]");
      if (!fromPlayBtn && this.dataset.interactable === "false") {
        return;
      }
    }
    this.strategy.onClick();
    this.render();
  }
  
  onHover = () => {
    // Don't handle hover if HTML controls are enabled and we're in sound mode
    if (this.showHtmlControls && !this.strategy.isPreview()) {
      return;
    }
    // Only handle hover if in preview mode
    if (this.strategy.isPreview()) {
      this.strategy.onHover();
      this.render();
    }
  }
  
  onHoverEnd = () => {
    // Don't handle hover end if HTML controls are enabled and we're in sound mode
    if (this.showHtmlControls && !this.strategy.isPreview()) {
      return;
    }
    // Only handle hover end if in preview mode
    if (this.strategy.isPreview()) {
      this.strategy.onHoverEnd();
      this.render();
    }
  }
  
  onResize = () => {
    if (this.controller.updateActiveVideo()) {
      this.render();
    }
  }
  
  onIntersection = (entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        this.strategy.onEnterViewport();
      } else {
        this.strategy.onExitViewport();
      }
      this.render();
    });
  }
  
  onVideoPause = () => {
    if (this.controller.activeVideo) {
      this.strategy.onVideoPause();
      this.render();
    }
  }
  
  onVideoPlay = () => {
    this.render();
  }
  
  onVideoEnd = () => {
    // Dispatch custom event when video ends
    this.dispatchEvent(new CustomEvent('videoEnd', {
      bubbles: true,
      detail: { video: this }
    }));
  }
  
  pauseOtherVideos() {
    document.querySelectorAll("ia-video").forEach(vid => {
      if (vid !== this && vid.strategy && !vid.strategy.isPreview() && !vid.controller.isPaused) {
        vid.onClick();
      }
    });
  }
  
  render() {
    this.renderButton();
    this.renderPlayingClass();
    this.renderPausedVideoContent();
  }
  
  renderButton() {
    if (!this.playBtn) return;
    const show = this.strategy.shouldShowButton();
    this.playBtn.classList.toggle("ia-hidden", !show);
  }
  
  renderPlayingClass() {
    const playing = this.strategy.shouldShowAsPlaying();
    this.classList.toggle("is-playing", playing);
  }
  
  renderPausedVideoContent() {
    // Find parent with js-video-component class
    const videoComponent = this.closest(".js-video-component");
    if (!videoComponent) return;
    
    // Find all children with js-paused-video-content class
    const pausedContentElements = videoComponent.querySelectorAll(".js-paused-video-content");
    if (pausedContentElements.length === 0) return;
    
    // Hide content when playing, show when paused
    const isPlaying = this.strategy.shouldShowAsPlaying();
    pausedContentElements.forEach(element => {
      element.style.visibility = isPlaying ? "hidden" : "visible";
    });
  }
  
  setupListeners() {
    this.addEventListener("click", this.onClick);
    this.addEventListener("mouseenter", this.onHover);
    this.addEventListener("mouseleave", this.onHoverEnd);
    
    // Setup video wrap hover listeners
    this.setupVideoWrapListeners();
    
    [this.desktopVideo, this.mobileVideo].filter(Boolean).forEach(video => {
      video.addEventListener("pause", this.onVideoPause);
      video.addEventListener("play", this.onVideoPlay);
      video.addEventListener("ended", this.onVideoEnd);
    });
  }
  
  setupVideoWrapListeners() {
    // Find parent with data-video-wrap attribute
    const videoWrap = this.closest("[data-video-wrap]");
    if (videoWrap) {
      const handleMouseEnter = () => {
        if (this.dataset.hoverPreview === "true") {
          this.onHover();
        }
      };
      const handleMouseLeave = () => {
        if (this.dataset.hoverPreview === "true") {
          this.onHoverEnd();
        }
      };
      
      // Store handlers for cleanup
      this.videoWrapHandlers = { handleMouseEnter, handleMouseLeave };
      
      videoWrap.addEventListener("mouseenter", handleMouseEnter);
      videoWrap.addEventListener("mouseleave", handleMouseLeave);
    }
  }
  
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(
      this.onIntersection,
      { threshold: 0.1 }
    );
    this.observer.observe(this);
  }
}

customElements.define("ia-video", IaVideo);
